import { SiteUrls } from '@tableau/shared-urls';
import React from 'react';

import { Messages } from '../../../../../gen-src/Localize';
import { ColumnOrderField, RemoteType } from '../../../../../gen-src/queries';
import {
  ApiRequestsContext,
  ColumnFactory,
  createIconCellFormatter,
  createLabelColumnFormatter,
  createSelectionColumn,
  createSortableHeaderFormatter,
  createTextCellFormatter,
  ExplorerStyledDisplayGrid,
  FeatureFlagsContext,
  GraphLabel,
  GraphQLError,
  GridSortProps,
  LabelContent,
  RemoteTypeMappings,
  reportGraphQLErrors,
  SelectionProviderChildProps,
  SiteUrlsContext,
  TypedBodyCellProps,
  TypedColumn,
  UnsortableColumnKey,
  UseRenderLabelsParams,
  UsualDisplayGridProps,
} from '../../../data-catalog-shared';
import { getLabelParamsForGrids, labelCategoryToString, LabelGridWrapperProps } from '../Labels/LabelsHookHelper';
import { createNumberCellFormatter } from '../datagrid/cellFormatters/NumberCellFormatter';
import {
  createQueryPreservingReplacingLinkCellFormatter,
} from '../datagrid/cellFormatters/QueryPreservingLinkCellFormatter';
import { RemoteTypeIcon } from '../datagrid/cellFormatters/RemoteTypeIcon';
import { createTagCellFormatter } from '../datagrid/cellFormatters/TagCellFormatter';
import { ColumnsValidSortFields } from '../sorting/sortProviders/ColumnsGridSortProvider';
import { ColumnInlineActionsMenu } from './ColumnInlineActionsMenu';

export interface TableColumnsGridRowData {
  readonly __typename: string;
  readonly tableId: string;
  readonly id: string;
  readonly luid: string;
  readonly tags: ReadonlyArray<string> | null;
  readonly name: string | null;
  readonly remoteType: RemoteType;
  readonly description: string | null;
  readonly datasourceCount: number | null;
  readonly sheetCount: number | null;
  readonly vizportalId: string | null;
  readonly labels?: ReadonlyArray<GraphLabel>;
}

type TableColumnsColumnFactory = ColumnFactory<
  GridSortProps<ColumnsValidSortFields>,
  TableColumnsGridRowData,
  ColumnsValidSortFields
>;

function columns(siteUrls: SiteUrls, canUpdate: boolean): ReadonlyArray<TableColumnsColumnFactory> {
  const cols: ReadonlyArray<TableColumnsColumnFactory | false> = [
    () => createSelectionColumn(),
    (sortParams) => ({
      name: 'type',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderType(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createIconCellFormatter(
        ({ remoteType }) => <RemoteTypeIcon remoteType={remoteType} />,
        ({ remoteType }) => RemoteTypeMappings[remoteType](),
      ),
      resizable: false,
      sortable: false,
    }),
    () => createLabelGridColumnFormatter(siteUrls, 'WARNING'),
    (sortParams) => ({
      name: 'name',
      dataKey: ColumnOrderField.NAME,
      headerText: Messages.GridHeaderName(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createQueryPreservingReplacingLinkCellFormatter(
        ({ name }) => name || '',
        (urls, { id, tableId }) => urls.catalogTableColumnPath(tableId, id),
      ),
      resizable: true,
      sortable: true,
    }),
    (sortParams) => ({
      name: 'actionsmenu',
      dataKey: UnsortableColumnKey,
      headerText: Messages.Actions(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createColumnActionsCellFormatter({
        getColumnIdFromRowData: (r) => r.__typename === 'Column' ? r.id : '',
        getColumnLuidFromRowData: (r) => r.luid,
        getColumnNameFromRowData: (r) => r.name ?? '',
        getColumnRemoteTypeFromRowData: (r) => r.remoteType,
        getTableVizportalId: (r) => r.vizportalId ? r.vizportalId : '',
        canUpdate,
      }),
      resizable: false,
      sortable: false,
    }),

    (sortParams) => ({
      name: 'connectedSheets',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderSheets(),
      headerFormatter: createSortableHeaderFormatter(sortParams, 'right'),
      cellFormatter: createNumberCellFormatter(({ sheetCount }) => sheetCount),
      sortable: false,
    }),
    (sortParams) => ({
      name: 'connectedDatasources',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderDatasources(),
      headerFormatter: createSortableHeaderFormatter(sortParams, 'right'),
      cellFormatter: createNumberCellFormatter(({ datasourceCount }) => datasourceCount),
      sortable: false,
    }),
    (sortParams) => ({
      name: 'description',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderDescription(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createTextCellFormatter(({ description }) => description, Messages.NoDescriptionGridPlaceholder()),
      sortable: false,
      growToFit: true,
    }),
    (sortParams) => ({
      name: 'tags',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderTags(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createTagCellFormatter(({ tags }) => tags ?? []),
      sortable: false,
      growToFit: true
    }),
    () => createLabelGridColumnFormatter(siteUrls, 'SENSITIVITY'),
  ];

  return cols.filter(Boolean) as ReadonlyArray<TableColumnsColumnFactory>;
}

export type TableColumnsDisplayGridProps =
  UsualDisplayGridProps<TableColumnsGridRowData, GraphQLError> &
  SelectionProviderChildProps &
  GridSortProps<ColumnsValidSortFields> &
  { tableVizportalId: string };

export function TableColumnsDisplayGrid(props: TableColumnsDisplayGridProps): JSX.Element {
  const { customSensitivityLabelsEnabled } = React.useContext(FeatureFlagsContext);
  const canUpdate = useCanUserSetColumnDescription(props.tableVizportalId);
  const siteUrls = React.useContext(SiteUrlsContext);
  const cols = columns(siteUrls, canUpdate)
    .map(columnFactory => columnFactory(props))
    .filter(column => {
      if (column.name === 'label-sensitivity' && !customSensitivityLabelsEnabled) {
        return false;
      }
      return true;
    });
  return (
    <ExplorerStyledDisplayGrid
      columns={cols}
      rowStore={props.rowStore}
      selectedKeys={props.selectedKeys}
      updateSelectionForKey={props.updateSelectionForKey}
      rowCount={props.rowCount}
      renderErrors={reportGraphQLErrors}
      orderBy={props.orderBy}
      updateSortOrder={props.updateSortOrder}
    />
  );
}

const ActionsCellStyle: React.CSSProperties = {
  display: 'flex',
  flexGrow: 1,
  alignItems: 'center',
  flexDirection: 'row',
  justifyContent: 'center',
};

function useCanUserSetColumnDescription(tableVizportalId: string) {
  const { canUserSetColumnDescription } = React.useContext(ApiRequestsContext);
  const [canUpdate, setCanUpdate] = React.useState(false);

  React.useEffect(() => {
    let subscribed = true;
    const fetchData = async () => {
      const data = await canUserSetColumnDescription({ tableVizportalId })
      if (subscribed) {
        setCanUpdate(data);
      }
    }
    void fetchData().catch()
    return () => { subscribed = false }
  },
    [canUserSetColumnDescription, tableVizportalId]
  );

  return (canUpdate);
}

function createColumnActionsCellFormatter<T>(
  opts: {
    getColumnIdFromRowData: (rowData: T) => string,
    getColumnLuidFromRowData: (rowData: T) => string,
    getColumnNameFromRowData: (rowData: T) => string,
    getColumnRemoteTypeFromRowData: (rowData: T) => RemoteType,
    getTableVizportalId: (rowData: T) => string,
    canUpdate: boolean,
  }
): React.Factory<TypedBodyCellProps<T>> {
  const { getColumnIdFromRowData, getColumnLuidFromRowData, getColumnNameFromRowData, getColumnRemoteTypeFromRowData, getTableVizportalId, canUpdate } = opts;

  return (props: TypedBodyCellProps<T>) => {
    const columnId = getColumnIdFromRowData(props.row.data);
    const tableVizportalId = getTableVizportalId(props.row.data);
    const luid = getColumnLuidFromRowData(props.row.data);
    const name = getColumnNameFromRowData(props.row.data);
    const remoteType = getColumnRemoteTypeFromRowData(props.row.data);

    return (
      <div style={ActionsCellStyle}>
        <div>
          <ColumnInlineActionsMenu
            columnId={columnId}
            columnLuid={luid}
            columnName={name}
            columnRemoteType={remoteType}
            tableVizportalId={tableVizportalId}
            canUpdate={canUpdate} />
        </div>
      </div>
    );
  }
}

function getLabelContentFromRowData(rowData: TableColumnsGridRowData, urls: SiteUrls): LabelContent {
  return {
    contentId: rowData.id,
    contentLuid: rowData.luid,
    contentType: 'column',
    contentName: rowData.name ?? undefined,
    contentUrl: urls.catalogTableColumnUrl(rowData.tableId, rowData.id),
    subjectArea: 'Column',
    remoteType: rowData.remoteType
  };
}

function createLabelGridColumnFormatter(urls: SiteUrls, category: LabelGridWrapperProps['category']): TypedColumn<TableColumnsGridRowData, ColumnOrderField.NAME> {

  function getPropsFromRowData(rowData: TableColumnsGridRowData): UseRenderLabelsParams {
    const content: LabelContent = getLabelContentFromRowData(rowData, urls);
    const labels: ReadonlyArray<GraphLabel> = rowData.labels ? rowData.labels : [];
    return getLabelParamsForGrids({ category, labels, urls, source: 'table-columns', content });
  }

  return createLabelColumnFormatter(labelCategoryToString(category), { overrides: {}, getPropsFromRowData }) as
    TypedColumn<TableColumnsGridRowData, ColumnOrderField.NAME>;
}
